import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import { Button } from '@gik/ui/Button';
import type { TripleInputValue } from '@gik/ui/ColorPicker/components/TripleSplitInput';
import { TripleSplitInput } from '@gik/ui/ColorPicker/components/TripleSplitInput';
import type { InputProps } from '@gik/ui/Input';
import { Input } from '@gik/ui/Input';
import { SvgIcon } from '@gik/ui/SvgIcon';
import SelectorIcon from '@heroicons/react/solid/SelectorIcon';
import React from 'react';
import type { HSLColor, RGBColor } from 'react-color';
import type { ColorWrapChangeHandler, InjectedColorProps } from 'react-color/lib/components/common/ColorWrap';

export interface IColorInputProps extends InjectedColorProps, UIComponent, Omit<InputProps, 'onChange'> {}

const blockName = 'color-input';

export function ColorInput({ id, className, style, hex, rgb, hsl, onChange, ...inputProps }: IColorInputProps) {
  const bem = useBemCN(blockName);
  const {
    mode,
    hexValue,
    onHEXChange,
    onInputFocus,
    onInputBlur,
    rgbValue,
    onRGBChange,
    hslValue,
    onHSLChange,
    onClickChangeMode,
  } = useColorInputController(hex, rgb, hsl, onChange);

  return (
    <div {...bem(null, null, className)} id={id} style={style}>
      <div {...bem('inputs')}>
        {mode === 0 && (
          <div {...bem('hex-input')}>
            <Input
              {...inputProps}
              value={hexValue}
              onValueChange={onHEXChange}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
            />
            <div>HEX</div>
          </div>
        )}
        {mode === 1 && (
          <div {...bem('rgb-input')}>
            <TripleSplitInput
              {...inputProps}
              label1="R"
              label2="G"
              label3="B"
              value={rgbValue}
              onChange={onRGBChange}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
            />
          </div>
        )}
        {mode === 2 && (
          <div {...bem('hsl-input')}>
            <TripleSplitInput
              {...inputProps}
              label1="H"
              label2="S"
              label3="L"
              value={hslValue}
              onChange={onHSLChange}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
            />
          </div>
        )}
      </div>
      <div {...bem('controls')}>
        <Button onClick={onClickChangeMode} variant={'default-extra-dark-plain'} circle>
          <SvgIcon Icon={SelectorIcon} />
        </Button>
      </div>
    </div>
  );
}

function useColorInputController(hex: string, rgb: RGBColor, hsl: HSLColor, onChange: ColorWrapChangeHandler = noop) {
  const [mode, setMode] = React.useState<number>(0);
  const [_hex, setHex] = React.useState<string>(hex);
  const [_rgb, setRGB] = React.useState<RGBColor>(rgb);
  const [_hsl, setHSL] = React.useState<HSLColor>(hsl);

  // this logic enables back and forth sync of values with other color selector components and react-color built-in
  // validation/correction without immediately interfering with user input
  const [useStateValue, setUseStateValue] = React.useState<boolean>(false);

  const onInputFocus = React.useCallback(() => {
    setHex(hex);
    setRGB(rgb);
    setHSL(hsl);
    setUseStateValue(true);
  }, [hex, hsl, rgb]);

  const onInputBlur = React.useCallback(() => {
    setUseStateValue(false);
  }, []);

  const onClickChangeMode = React.useCallback(() => {
    if (mode >= 2) {
      setMode(0);
    } else {
      setMode(mode => mode + 1);
    }

    setHex(hex);
    setRGB(rgb);
    setHSL(hsl);
  }, [hex, hsl, mode, rgb]);

  const onHEXChange = React.useCallback(
    (values: string) => {
      onChange?.(values);
    },
    [onChange]
  );

  const onRGBChange = React.useCallback(
    (values: TripleInputValue) => {
      onChange?.({
        r: parseInt(values.value1),
        g: parseInt(values.value2),
        b: parseInt(values.value3),
      });
    },
    [onChange]
  );

  const onHSLChange = React.useCallback(
    (values: TripleInputValue) => {
      onChange?.({
        h: parseInt(values.value1),
        s: parseInt(values.value2) / 100,
        l: parseInt(values.value3) / 100,
      });
    },
    [onChange]
  );

  const hexValue = React.useMemo(
    () => (useStateValue ? _hex?.toUpperCase() : hex?.toUpperCase()),
    [_hex, hex, useStateValue]
  );

  const rgbValue = React.useMemo(
    () =>
      useStateValue
        ? _rgb
          ? {
              value1: _rgb.r.toString(),
              value2: _rgb.g.toString(),
              value3: _rgb.b.toString(),
            }
          : undefined
        : rgb
        ? {
            value1: rgb.r.toString(),
            value2: rgb.g.toString(),
            value3: rgb.b.toString(),
          }
        : undefined,
    [_rgb, rgb, useStateValue]
  );

  const hslValue = React.useMemo(
    () =>
      useStateValue
        ? _hsl
          ? {
              value1: Math.floor(_hsl.h).toString(),
              value2: Math.floor(_hsl.s * 100).toString(),
              value3: Math.floor(_hsl.l * 100).toString(),
            }
          : undefined
        : hsl
        ? {
            value1: Math.floor(hsl.h).toString(),
            value2: Math.floor(hsl.s * 100).toString(),
            value3: Math.floor(hsl.l * 100).toString(),
          }
        : undefined,
    [_hsl, hsl, useStateValue]
  );

  return {
    mode,
    hexValue,
    onHEXChange,
    onInputFocus,
    onInputBlur,
    rgbValue,
    onRGBChange,
    hslValue,
    onHSLChange,
    onClickChangeMode,
  };
}
